import { ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailField } from '@/components/shared/detail-field';
import { Pagination } from '@/components/shared/pagination';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api, type OffsetPage } from '@/lib/api';
import { formatDateTime } from '@/utils/format';

const HISTORY_ACTIONS = [
  'platform.subscription.updated',
  'platform.church.suspended',
  'platform.church.reactivated',
  'platform.church.payments_suspended',
  'platform.church.payments_resumed',
  'platform.impersonation.started',
  'platform.impersonation.ended',
] as const;

const HISTORY_FIELDS = [
  'plan',
  'status',
  'includedSeats',
  'trialEndsAt',
  'currentPeriodEnd',
] as const;

type HistoryAction = (typeof HISTORY_ACTIONS)[number];
type HistoryField = (typeof HISTORY_FIELDS)[number];

export interface PlatformChurchHistoryEntry {
  id: string;
  action: HistoryAction;
  actorName: string | null;
  reason: string | null;
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
  createdAt: string;
}

const ACTION_KEYS: Record<HistoryAction, string> = {
  'platform.subscription.updated': 'subscriptionUpdated',
  'platform.church.suspended': 'churchSuspended',
  'platform.church.reactivated': 'churchReactivated',
  'platform.church.payments_suspended': 'paymentsSuspended',
  'platform.church.payments_resumed': 'paymentsResumed',
  'platform.impersonation.started': 'impersonationStarted',
  'platform.impersonation.ended': 'impersonationEnded',
};

const isHistoryAction = (value: string): value is HistoryAction =>
  (HISTORY_ACTIONS as readonly string[]).includes(value);

const isHistoryField = (value: string): value is HistoryField =>
  (HISTORY_FIELDS as readonly string[]).includes(value);

const BILLING_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'grace',
  'suspended',
  'cancelled',
  'expired',
] as const;

const CHURCH_STATUSES = ['active', 'suspended', 'banned', 'deactivated'] as const;
const PLANS = ['trial', 'community', 'starter', 'essential', 'professional', 'enterprise'] as const;

type Translate = (key: string, options?: Record<string, string | number>) => string;

const formatHistoryValue = (field: HistoryField, value: unknown, t: Translate): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (field === 'plan' && typeof value === 'string' && (PLANS as readonly string[]).includes(value)) {
    return t(`billing.plans.${value}`);
  }
  if (field === 'status' && typeof value === 'string') {
    if ((BILLING_STATUSES as readonly string[]).includes(value)) {
      return t(`billing.statuses.${value}`);
    }
    if ((CHURCH_STATUSES as readonly string[]).includes(value)) {
      return t(`status.${value}`);
    }
  }
  if (
    (field === 'trialEndsAt' || field === 'currentPeriodEnd') &&
    typeof value === 'string'
  ) {
    return formatDateTime(value);
  }
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  return null;
};

const changedFields = (entry: PlatformChurchHistoryEntry): HistoryField[] => {
  const keys = new Set([
    ...Object.keys(entry.beforeValue ?? {}),
    ...Object.keys(entry.afterValue ?? {}),
  ]);
  return [...keys].filter(isHistoryField).filter((field) => {
    return JSON.stringify(entry.beforeValue?.[field] ?? null) !== JSON.stringify(entry.afterValue?.[field] ?? null);
  });
};

export const ChurchHistoryDialog = ({
  churchId,
  churchName,
  open,
  onOpenChange,
}: {
  churchId: string | null;
  churchName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<PlatformChurchHistoryEntry | null>(null);

  useEffect(() => {
    if (open) return;
    setPage(1);
    setSelected(null);
  }, [open]);

  const history = useQuery({
    queryKey: ['platform-church-history', churchId, page, pageSize],
    queryFn: () =>
      api.get<{ data: PlatformChurchHistoryEntry[]; page: OffsetPage }>(
        `/platform/churches/${churchId}/history`,
        { query: { page, pageSize } },
      ),
    enabled: open && Boolean(churchId),
  });

  const selectedFromPage =
    history.data?.data.find((row) => row.id === selected?.id) ?? selected;

  const columns: DataTableColumn<PlatformChurchHistoryEntry>[] = useMemo(
    () => [
      {
        id: 'createdAt',
        header: t('churches.historyDialog.columns.when'),
        cell: (row) => formatDateTime(row.createdAt),
      },
      {
        id: 'action',
        header: t('churches.historyDialog.columns.action'),
        cell: (row) =>
          isHistoryAction(row.action)
            ? t(`churches.historyDialog.actions.${ACTION_KEYS[row.action]}`)
            : t('churches.historyDialog.actions.subscriptionUpdated'),
      },
      {
        id: 'actor',
        header: t('churches.historyDialog.columns.actor'),
        cell: (row) => row.actorName ?? t('churches.historyDialog.emptyValue'),
        hideBelowMd: true,
      },
      {
        id: 'reason',
        header: t('churches.historyDialog.columns.reason'),
        cell: (row) => row.reason ?? t('churches.historyDialog.emptyValue'),
      },
    ],
    [t],
  );

  const pageMeta = history.data?.page;
  const close = () => onOpenChange(false);
  const detailFields = selectedFromPage ? changedFields(selectedFromPage) : [];

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && close()}>
        <DialogContent closeLabel={tCommon('action.close')} size="xl">
          <DialogHeader>
            <DialogTitle>{t('churches.historyDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('churches.historyDialog.description', { name: churchName })}
            </DialogDescription>
          </DialogHeader>

          <DialogBody contentClassName="flex flex-col gap-6">
            {history.isError ? (
              <p className="text-body-sm text-danger">{tErrors('UNEXPECTED')}</p>
            ) : (
              <>
                <DataTable
                  columns={columns}
                  rows={history.data?.data ?? []}
                  rowKey={(row) => row.id}
                  loading={history.isLoading}
                  empty={
                    <p className="py-8 text-center text-body-sm text-fg-secondary">
                      {t('churches.historyDialog.empty')}
                    </p>
                  }
                  onRowClick={setSelected}
                />
                {pageMeta && pageMeta.totalPages > 1 ? (
                  <Pagination
                    page={pageMeta.page}
                    pageSize={pageMeta.pageSize}
                    totalItems={pageMeta.totalItems}
                    totalPages={pageMeta.totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={(next) => {
                      setPageSize(next);
                      setPage(1);
                    }}
                    labels={{
                      showing: ({ from, to, total }) =>
                        tCommon('pagination.showing', { from, to, total }),
                      perPage: tCommon('pagination.perPage'),
                      first: tCommon('pagination.first'),
                      previous: tCommon('pagination.previous'),
                      next: tCommon('pagination.next'),
                      last: tCommon('pagination.last'),
                    }}
                  />
                ) : null}
              </>
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={close}>
              {tCommon('action.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={selected !== null}
        onOpenChange={(next) => {
          if (!next) setSelected(null);
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} size="md">
          <DialogHeader>
            <DialogTitle>
              {selectedFromPage && isHistoryAction(selectedFromPage.action)
                ? t(`churches.historyDialog.actions.${ACTION_KEYS[selectedFromPage.action]}`)
                : t('churches.historyDialog.detailTitle')}
            </DialogTitle>
            <DialogDescription>{t('churches.historyDialog.detailDescription')}</DialogDescription>
          </DialogHeader>

          <DialogBody contentClassName="flex flex-col gap-4">
            {selectedFromPage ? (
              <dl className="space-y-3">
                <DetailField label={t('churches.historyDialog.columns.when')}>
                  {formatDateTime(selectedFromPage.createdAt)}
                </DetailField>
                <DetailField label={t('churches.historyDialog.columns.actor')}>
                  {selectedFromPage.actorName ?? t('churches.historyDialog.emptyValue')}
                </DetailField>
                <DetailField label={t('churches.historyDialog.columns.reason')}>
                  {selectedFromPage.reason ?? t('churches.historyDialog.emptyValue')}
                </DetailField>
                {detailFields.map((field) => {
                  const before = formatHistoryValue(field, selectedFromPage.beforeValue?.[field], t);
                  const after = formatHistoryValue(field, selectedFromPage.afterValue?.[field], t);
                  return (
                    <DetailField key={field} label={t(`churches.historyDialog.fields.${field}`)}>
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <span className="text-fg-tertiary line-through">
                          {before ?? t('churches.historyDialog.emptyValue')}
                        </span>
                        <ArrowRight aria-hidden className="h-3 w-3 text-fg-tertiary" />
                        <span>{after ?? t('churches.historyDialog.emptyValue')}</span>
                      </span>
                    </DetailField>
                  );
                })}
              </dl>
            ) : (
              <p className="text-body-sm text-fg-secondary">{t('churches.historyDialog.empty')}</p>
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>
              {tCommon('action.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
